import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
  ViewChild
} from '@angular/core';
import {isPlatformBrowser, NgOptimizedImage, NgTemplateOutlet} from '@angular/common';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {of, Subject} from 'rxjs';
import {catchError, filter, map, takeUntil} from 'rxjs/operators';
import {MatDialog} from '@angular/material/dialog';

import {BundleService} from '../../../services/bundle.service';
import {NotificationService} from '../../../notification/notification.service';
import {NotificationType} from '../../../enum/notification-type.enum';
import {DetailsProductMenusComponent} from './details-product-menus/details-product-menus.component';
import {ProductService} from '../../../services/product.service';
import {TypeOrderEnum} from '../../../enum/type-order.enum';
import {ConfigurationStore} from '../../../store/configuration.store';
import {OrderContextStore} from '../../../store/order-context.store';
import {
  BundleCartItem,
  CartItem,
  CartItemType,
  CartService,
  ProductCartItem,
  PromotionCartItem
} from '../../../services/cart.service';
import {HideFloatingCartService} from '../../../services/hide-floating-cart.service';
import {OrderComponent} from '../../../order/order.component';
import {Meta, MetaDefinition, Title} from '@angular/platform-browser';
import {THEME_CONFIG} from '../../../../environments/tokens/theme.token';
import {TopNavbarComponent} from '../../../shared/top-navbar/top-navbar.component';
import {NavbarComponent} from '../../../shared/navbar/navbar.component';
import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';
import {TenantService} from '../../../services/tenant.service';
import {AdvertisementService} from '../../../services/advertisement.service';
import {AuthenticationService} from '../../../services/authentication.service';
import {AdvertisementFlowService} from '../../../services/advertisement-flow.service';
import {CustomerService} from '../../../services/customer.service';

interface ProcessedRule {
  id: string;
  name: string;
  type: 'AND' | 'OR';
  minSelections: number;
  maxSelections: number;
  components: any[];
  isNested?: boolean;
  parentRuleId?: string;
  level: number;
  nestedRules?: ProcessedRule[];
}

@Component({
  selector: 'app-details-menus',
  templateUrl: './details-menus.component.html',
  styleUrl: './details-menus.component.scss',
  imports: [TopNavbarComponent, NavbarComponent, MatIcon, NgTemplateOutlet,
    MatButton, NgOptimizedImage]
})
export class DetailsMenusComponent implements OnInit, OnDestroy {
  private platformId = inject(PLATFORM_ID);
  private destroy$ = new Subject<void>();
  isBrowser = isPlatformBrowser(this.platformId);
  protected readonly themeConfig = inject(THEME_CONFIG)
  private adFlowService = inject(AdvertisementFlowService);
  private bundleService = inject(BundleService);
  private productService = inject(ProductService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);
  private cartService = inject(CartService);
  private route = inject(ActivatedRoute);
  private dialog = inject(MatDialog);
  private cdr = inject(ChangeDetectorRef);
  private orderContextStore = inject(OrderContextStore);
  private configStore = inject(ConfigurationStore);
  private titleService = inject(Title)
  private metaService = inject(Meta)
  private customerService = inject(CustomerService);
  private metaTags: HTMLMetaElement[] = []
  hideFloatingCartService = inject(HideFloatingCartService);
  readonly restaurantAddress = this.configStore.address;
  private tenantService = inject(TenantService);
  isMilano = false;
  private advertisementService = inject(AdvertisementService);
  private authenticationService = inject(AuthenticationService);
  private advertisementQueue: any[] = [];
  private fromMenuFlow = false;
  private readonly SEEN_ADS_STORAGE_KEY = 'seen_advertisements';
  @ViewChild('scrollableContent') scrollableContent!: ElementRef<HTMLDivElement>;
  readonly loyaltyUnitCost = this.configStore.loyaltyUnitCost;
  readonly isSubmitting = signal<boolean>(false);
  bundleName!: string;
  bundleId!: string;
  bundle!: any;
  readonly isLoading = signal<boolean>(true);
  Object = Object;

  processedRules: ProcessedRule[] = [];
  selectedOrGroups: { [ruleId: string]: string[] } = {};
  selectedItemQuantities: { [componentId: string]: { [itemId: string]: number } } = {};

  customizableItems: { [productId: string]: boolean } = {};
  customizedItems: { [key: string]: { [index: number]: any } } = {};
  productRequiredAdditions: { [productId: string]: boolean } = {};
  private fullProductsCache: { [productId: string]: any } = {};

  isCartSheetOpen: boolean = false;
  touchStartY = 0;
  touchCurrentY = 0;
  sheetTransform = 'translateY(0px)';
  SWIPE_CLOSE_THRESHOLD = 120;

  selectedOrderType = this.orderContextStore.orderType;
  cityName = this.orderContextStore.cityName;
  zipCode = this.orderContextStore.zipcode;
  cityId = this.orderContextStore.cityId;
  deliveryDelay = this.orderContextStore.deliveryDelay;
  minOrderAmounts = this.orderContextStore.minOrderAmounts;

  editingCartItemId: string | null = null;
  private returnToCommand = false;
  private editingData: {
    existingSelectedItems?: any;
    existingCustomizedItems?: any;
  } | null = null;

  get isDelivery(): boolean {
    return this.selectedOrderType() === TypeOrderEnum.DELIVERY;
  }

  get isTakeawayOrOnSite(): boolean {
    const orderType = this.selectedOrderType();
    return orderType === TypeOrderEnum.TAKEAWAY || orderType === TypeOrderEnum.ON_SITE;
  }

  constructor() {
  }

  ngOnInit() {
    this.isMilano = this.tenantService.getActiveTenant() === 'milano';

    this.hideFloatingCartService.setHide(true);

    if (this.isBrowser) {
      window.scrollTo(0, 0);
    }

    this.bundleName = this.route.snapshot.paramMap.get('name') || '';

    this.loadBundleWithState();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.loadBundleWithState();
    });
  }

  get isRedeemMode(): boolean {
    return this.route.snapshot.queryParamMap.get('mode') === 'redeem' && !!this.loyaltyUnitCost();
  }

  toPoints(price: number): number {
    const unit = this.loyaltyUnitCost();
    return unit ? Math.round(price * 100 * unit) : 0;
  }

  formatBundlePrice(): string {
    const price = this.bundle?.price ?? 0;
    return this.isRedeemMode
      ? `${this.toPoints(price)} pts`
      : `${this.truncate2(price).toFixed(2)} ${this.bundle.public}`;
  }

  /** Suppléments payants en € (total moins le prix de base) */
  getExtrasPrice(): number {
    return this.truncate2(this.getTotalCartPrice() - (this.bundle?.price ?? 0));
  }

  /** Total affiché : "3.00 € (1045 pts)" en mode redeem */
  formatBundleTotal(): string {
    if (!this.isRedeemMode) {
      return `${this.truncate2(this.getTotalCartPrice()).toFixed(2)} ${this.bundle.public}`;
    }
    return `${this.getExtrasPrice().toFixed(2)} ${this.bundle.public} (${this.toPoints(this.bundle.price)} pts)`;
  }

  private loadBundleWithState() {
    let state: any = null;
    const currentNavigation = this.router.currentNavigation();

    if (currentNavigation?.extras?.state) {
      state = currentNavigation.extras.state;
    }
    if (!state && this.isBrowser) {
      state = (window as any).history.state || {};
    }

    this.returnToCommand = state?.returnToCommand === true;
    this.bundleId = state?.bundleSlug || this.route.snapshot.paramMap.get('name');
    this.editingCartItemId = state?.editingCartItemId || null;
    this.fromMenuFlow = state?.fromMenu === true;

    if (this.editingCartItemId && state) {
      this.editingData = {
        existingSelectedItems: state.existingSelectedItems,
        existingCustomizedItems: state.existingCustomizedItems,
      };
    } else {
      this.editingData = null;
    }

    if (this.bundleId) {
      this.getBundleById(this.bundleId);
    } else {
      console.warn('Aucun bundleId trouvé dans l\'état ou l\'URL');
    }
  }

  getBundleById(id: string) {
    this.isLoading.set(true);

    this.bundleService.getBundleBySlug(id).pipe(
      catchError((err) => {
        this.notificationService.errorHandler(err);
        this.isLoading.set(false);
        return of(null);
      }),
      takeUntil(this.destroy$)
    ).subscribe((result) => {
      if (!result) return;
      this.bundle = result.body?.data;

      if (this.bundle.selectionRules) {
        this.processedRules = this.processRulesHierarchy(this.bundle.selectionRules);
        this.initializeAllQuantities(this.processedRules);
        this.initializeOrSelections(this.processedRules);
      }

      this.isLoading.set(false);

      if (this.editingCartItemId && this.editingData) {
        this.prefillBundleEditingData();
      }

      this.setSeoTags();

      if (this.fromMenuFlow) {
        this.checkAndShowAdvertisement();
      }
    });
  }

  private getSeenAdvertisementIds(): string[] {
    if (!this.isBrowser) return [];
    try {
      const raw = localStorage.getItem(this.SEEN_ADS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }

  private markAdvertisementAsSeen(adId: string): void {
    if (!this.isBrowser) return;
    const seenIds = this.getSeenAdvertisementIds();
    if (!seenIds.includes(adId)) {
      seenIds.push(adId);
      localStorage.setItem(this.SEEN_ADS_STORAGE_KEY, JSON.stringify(seenIds));
    }
  }

  private checkAndShowAdvertisement(): void {
    if (!this.isBrowser) return;
    this.adFlowService.checkAndShowAdvertisements(this.cityId(), this.isDelivery);
  }

  private prefillBundleEditingData(): void {
    if (!this.editingData) return;
    if (this.editingData.existingSelectedItems) {
      Object.keys(this.editingData.existingSelectedItems).forEach(componentId => {
        if (!this.selectedItemQuantities[componentId]) {
          this.selectedItemQuantities[componentId] = {};
        }
        const componentData = this.editingData!.existingSelectedItems[componentId];
        Object.keys(componentData).forEach(itemId => {
          this.selectedItemQuantities[componentId][itemId] = Number(componentData[itemId]) || 0;
        });
      });
    }
    if (this.editingData.existingCustomizedItems) {
      this.customizedItems = {};
      Object.keys(this.editingData.existingCustomizedItems).forEach(fullKey => {
        const parts = fullKey.split('_');
        if (parts.length < 3) return;
        const componentId = parts[0];
        const itemId = parts[1];
        const index = parseInt(parts[2], 10);
        const customKey = `${componentId}_${itemId}_${index}`;
        // 🔴 Utiliser une copie profonde (JSON.parse/JSON.stringify) pour éviter les effets de bord
        this.customizedItems[customKey] = JSON.parse(
          JSON.stringify(this.editingData!.existingCustomizedItems[fullKey])
        );
        this.customizableItems[itemId] = true;
      });
    }
    Object.keys(this.selectedItemQuantities).forEach(componentId => {
      const componentItems = this.selectedItemQuantities[componentId];
      Object.keys(componentItems).forEach(itemId => {
        const qty = componentItems[itemId];
        if (qty > 0) {
          setTimeout(() => {
            this.checkIfCustomizable(itemId, componentId, false, 0);
          }, 0);
        }
      });
    });
    this.reconstructOrGroupSelections();
    this.cdr.detectChanges();
  }

  checkIfCustomizable(itemId: string, componentId: string, autoOpenIfCustomizable: boolean = false, index: number = 0) {
    const itemKey = itemId;

    if (this.customizableItems[itemKey] !== undefined) {
      if (autoOpenIfCustomizable && this.customizableItems[itemKey] && this.productRequiredAdditions[itemKey]) {
        const cachedProduct = this.fullProductsCache[itemId];
        if (cachedProduct) {
          this.openProductCustomization({id: itemId}, componentId, index, true);
        } else {
          this.productService.getProductById(itemId).pipe(takeUntil(this.destroy$)).subscribe({
            next: (res: any) => {
              this.fullProductsCache[itemId] = res.body?.data;
              this.openProductCustomization({id: itemId}, componentId, index, true);
            }
          });
        }
      }
      return;
    }
    if (this.fullProductsCache[itemId]) {
      const product = this.fullProductsCache[itemId];
      const component = this.getComponentById(componentId);
      const componentSize = component?.size?.name || null;

      const filteredAdditions = this.getFilteredAdditionsForProduct(product?.additions || [], componentSize);
      const isCustomizable = filteredAdditions.length > 0 ||
        product?.ingredients?.length > 0 ||
        product?.supplements?.length > 0;
      const hasRequiredAdditions = filteredAdditions.some((add: any) => add.required);

      this.customizableItems[itemKey] = isCustomizable;
      this.productRequiredAdditions[itemKey] = hasRequiredAdditions;

      if (autoOpenIfCustomizable && hasRequiredAdditions) {
        this.openProductCustomization({id: itemId}, componentId, index, true);
      }
      return;
    }
    this.productService.getProductById(itemId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        const product = res.body?.data;
        this.fullProductsCache[itemId] = product;

        const component = this.getComponentById(componentId);
        const componentSize = component?.size?.name || null;

        const filteredAdditions = this.getFilteredAdditionsForProduct(product?.additions || [], componentSize);
        const isCustomizable = filteredAdditions.length > 0 ||
          product?.ingredients?.length > 0 ||
          product?.supplements?.length > 0;
        const hasRequiredAdditions = filteredAdditions.some((add: any) => add.required);

        this.customizableItems[itemKey] = isCustomizable;
        this.productRequiredAdditions[itemKey] = hasRequiredAdditions;

        if (autoOpenIfCustomizable && hasRequiredAdditions) {
          this.openProductCustomization({id: itemId}, componentId, index, true);
        }
      },
      error: () => {
        this.customizableItems[itemKey] = false;
        this.productRequiredAdditions[itemKey] = false;
      }
    });
  }

  incrementItem(componentId: string, itemId: string, maxQuantity: number, ruleIndex: number) {
    if (!this.canIncrementInOrContext(componentId, itemId, maxQuantity)) {
      return;
    }
    const totalQty = this.getTotalQuantityForComponent(componentId);
    if (totalQty >= maxQuantity) {
      const component = this.getComponentById(componentId);
      const categoryName = component?.category?.name || 'cette catégorie';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Vous avez déjà choisi votre "${categoryName}" (${maxQuantity} max)`,
        ''
      );
      return;
    }

    if (!this.selectedItemQuantities[componentId]) {
      this.selectedItemQuantities[componentId] = {};
    }

    const currentQty = this.selectedItemQuantities[componentId][itemId] || 0;
    this.selectedItemQuantities[componentId][itemId] = currentQty + 1;
    this.checkIfCustomizable(itemId, componentId, true, currentQty);
    const newTotalQty = totalQty + 1;
    const component = this.getComponentById(componentId);
    const requiredQty = component?.quantity || 1;

    if (newTotalQty === requiredQty) {
      setTimeout(() => {
        this.navigateToNextElement(componentId, itemId);
      }, 300);
    }

    if (this.isBrowser && window.innerWidth < 1024 && this.canAddToCart() && !this.isCartSheetOpen) {
      setTimeout(() => this.toggleCartSheet(), 400);
    }
  }

  closeCartSheet() {
    this.isCartSheetOpen = false;
    this.sheetTransform = 'translateY(0px)';
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    if (this.scrollableContent) {
      this.scrollableContent.nativeElement.scrollTop = 0;
    }
  }

  toggleCartSheet() {
    this.isCartSheetOpen = !this.isCartSheetOpen;
    if (this.isBrowser) {
      document.body.style.overflow = this.isCartSheetOpen ? 'hidden' : '';
    }
    if (this.isCartSheetOpen && this.scrollableContent) {
      setTimeout(() => {
        if (this.scrollableContent) {
          this.scrollableContent.nativeElement.scrollTop = 0;
        }
      });
    }
  }
  scrollToComponent(componentId: string) {
    if (!this.isBrowser) return;

    const breakpoint = this.getCurrentBreakpoint();
    const prefixedId = `${breakpoint}-component-${componentId}`;
    const element = document.getElementById(prefixedId);
    if (!element) return;

    const width = window.innerWidth;
    let offset = 120;
    if (width < 768) offset = 80;
    else if (width < 1024) offset = 100;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  scrollToRule(ruleId: string) {
    if (!this.isBrowser) return;

    const breakpoint = this.getCurrentBreakpoint();
    const prefixedId = `${breakpoint}-rule-${ruleId}`;
    const element = document.getElementById(prefixedId);
    if (!element) return;

    const width = window.innerWidth;
    let offset = 120;
    if (width < 768) offset = 80;
    else if (width < 1024) offset = 100;

    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  private getCurrentBreakpoint(): 'mobile' | 'tablet' | 'desktop' {
    if (!this.isBrowser) return 'desktop';
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }

  onTouchStart(event: TouchEvent) {
    this.touchStartY = event.touches[0].clientY;
  }

  onTouchMove(event: TouchEvent) {
    this.touchCurrentY = event.touches[0].clientY;
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > 0) {
      this.sheetTransform = `translateY(${deltaY}px)`;
    }
  }

  onTouchEnd() {
    const deltaY = this.touchCurrentY - this.touchStartY;
    if (deltaY > this.SWIPE_CLOSE_THRESHOLD) {
      this.closeCartSheet();
    } else {
      this.sheetTransform = 'translateY(0px)';
    }
  }

  private reconstructOrGroupSelections(): void {
    const reconstruct = (rules: ProcessedRule[]) => {
      rules.forEach(rule => {
        if (rule.type === 'OR' && rule.nestedRules && rule.nestedRules.length > 0) {
          if (!this.selectedOrGroups[rule.id]) {
            this.selectedOrGroups[rule.id] = [];
          }
          rule.nestedRules.forEach(nestedRule => {
            if (this.nestedRuleHasSelectedItems(nestedRule)) {
              if (!this.selectedOrGroups[rule.id].includes(nestedRule.id)) {
                this.selectedOrGroups[rule.id].push(nestedRule.id);
              }
            }
          });
        }
        if (rule.nestedRules) reconstruct(rule.nestedRules);
      });
    };
    reconstruct(this.processedRules);
  }

  private nestedRuleHasSelectedItems(rule: ProcessedRule): boolean {
    for (const component of rule.components) {
      const componentQty = this.selectedItemQuantities[component.id];
      if (componentQty && Object.values(componentQty).some(qty => qty > 0)) return true;
    }
    if (rule.nestedRules) {
      for (const nested of rule.nestedRules) {
        if (this.nestedRuleHasSelectedItems(nested)) return true;
      }
    }
    return false;
  }

  initializeOrSelections(rules: ProcessedRule[]) {
    rules.forEach(rule => {
      if (rule.type === 'OR' && !this.selectedOrGroups[rule.id]) {
        this.selectedOrGroups[rule.id] = [];
      }
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        this.initializeOrSelections(rule.nestedRules);
      }
    });
  }

  initializeAllQuantities(rules: ProcessedRule[]) {
    rules.forEach(rule => {
      rule.components.forEach((component: any) => {
        if (!this.selectedItemQuantities[component.id]) {
          this.selectedItemQuantities[component.id] = {};
        }
        component.availableItems.forEach((item: any) => {
          this.selectedItemQuantities[component.id][item.id] = 0;
        });
      });
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        this.initializeAllQuantities(rule.nestedRules);
      }
    });
  }

  getRuleSelectionInfo(rule: ProcessedRule): string {
    if (rule.type === 'OR') {
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        return `Choisissez ${rule.minSelections} à ${rule.maxSelections} parmi les options`;
      }
      const hasSingleComponent = rule.components.length === 1;
      const component = hasSingleComponent ? rule.components[0] : null;
      if (component && component.required && component.quantity) {
        return `Choisissez ${component.quantity} produit${component.quantity > 1 ? 's' : ''}`;
      }
      return `Choisissez ${rule.minSelections} des ces produits`;
    }
    return `Tous les éléments sont requis`;
  }

  isRuleCompleted(ruleId: string): boolean {
    const rule = this.findRuleById(ruleId);
    if (!rule) return false;

    let totalSelected = 0;
    rule.components.forEach(component => {
      totalSelected += this.getTotalQuantityForComponent(component.id);
    });

    if (rule.nestedRules && rule.nestedRules.length > 0) {
      rule.nestedRules.forEach(nested => {
        if (this.isRuleCompleted(nested.id)) totalSelected++;
      });
    }
    return totalSelected >= rule.minSelections;
  }

  getRuleClass(rule: ProcessedRule): string {
    return `rule-block rule-level-${rule.level} rule-${rule.type.toLowerCase()} ${this.isRuleCompleted(rule.id) ? 'rule-completed' : ''}`;
  }

  getTotalQuantityForComponent(componentId: string): number {
    if (!this.selectedItemQuantities[componentId]) return 0;
    return Object.values(this.selectedItemQuantities[componentId]).reduce((sum: number, qty: number) => sum + qty, 0);
  }

  processRulesHierarchy(rules: any[], level: number = 0, parentRuleId?: string): ProcessedRule[] {
    const processed: ProcessedRule[] = [];
    const sortedRules = [...rules].sort((a, b) => a.order - b.order);

    sortedRules.forEach(rule => {
      const processedRule: ProcessedRule = {
        id: rule.id,
        name: rule.name,
        type: rule.type,
        minSelections: rule.minSelections,
        maxSelections: rule.maxSelections,
        components: rule.components ? [...rule.components].sort((a, b) => a.order - b.order) : [],
        isNested: level > 0,
        parentRuleId: parentRuleId,
        level: level,
        nestedRules: []
      };
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        processedRule.nestedRules = this.processRulesHierarchy(rule.nestedRules, level + 1, rule.id);
      }
      processed.push(processedRule);
    });
    return processed;
  }

  isOrGroupSelected(parentRuleId: string, groupRuleId: string): boolean {
    return !!this.selectedOrGroups[parentRuleId]?.includes(groupRuleId);
  }

  toggleOrGroup(parentRuleId: string, groupRuleId: string, parentRule: ProcessedRule) {
    if (!this.selectedOrGroups[parentRuleId]) this.selectedOrGroups[parentRuleId] = [];
    const index = this.selectedOrGroups[parentRuleId].indexOf(groupRuleId);

    if (index > -1) {
      this.selectedOrGroups[parentRuleId].splice(index, 1);
      this.resetGroupQuantities(groupRuleId);
    } else {
      while (this.selectedOrGroups[parentRuleId].length >= parentRule.maxSelections) {
        const oldGroupId = this.selectedOrGroups[parentRuleId].shift();
        if (oldGroupId) this.resetGroupQuantities(oldGroupId);
      }
      this.selectedOrGroups[parentRuleId].push(groupRuleId);
    }
  }

  resetGroupQuantities(groupRuleId: string) {
    const group = this.findRuleById(groupRuleId);
    if (!group) return;
    group.components.forEach((component: any) => {
      if (this.selectedItemQuantities[component.id]) {
        Object.keys(this.selectedItemQuantities[component.id]).forEach(itemId => {
          this.selectedItemQuantities[component.id][itemId] = 0;
        });
      }
      Object.keys(this.customizedItems).forEach(key => {
        if (key.startsWith(component.id + '_')) delete this.customizedItems[key];
      });
    });
    if (group.nestedRules && group.nestedRules.length > 0) {
      group.nestedRules.forEach((nested: ProcessedRule) => {
        this.resetGroupQuantities(nested.id);
        if (nested.type === 'OR' && this.selectedOrGroups[nested.id]) this.selectedOrGroups[nested.id] = [];
      });
    }
    if (group.type === 'OR' && this.selectedOrGroups[group.id]) this.selectedOrGroups[group.id] = [];
  }

  findRuleById(ruleId: string, rules: ProcessedRule[] = this.processedRules): ProcessedRule | null {
    for (const rule of rules) {
      if (rule.id === ruleId) return rule;
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        const found = this.findRuleById(ruleId, rule.nestedRules);
        if (found) return found;
      }
    }
    return null;
  }

  getOrGroupSelectionCount = (ruleId: string) => `${this.selectedOrGroups[ruleId]?.length || 0}`;

  canIncrementInOrContext(componentId: string, itemId: string, quantity: number): boolean {
    const component = this.getComponentById(componentId);
    if (!component) return true;

    const componentRule = this.findRuleContainingComponent(componentId);
    if (!componentRule || !componentRule.parentRuleId) return true;

    const parentRule = this.findRuleById(componentRule.parentRuleId);
    if (!parentRule || parentRule.type !== 'OR') return true;

    if (!this.isOrGroupSelected(parentRule.id, componentRule.id)) {
      this.notificationService.sendNotification(NotificationType.WARNING, `Veuillez d'abord sélectionner l'option "${componentRule.name}"`, '');
      return false;
    }
    return true;
  }

  findRuleContainingComponent(componentId: string, rules: ProcessedRule[] = this.processedRules): ProcessedRule | null {
    for (const rule of rules) {
      if (rule.components.some((c: any) => c.id === componentId)) return rule;
      if (rule.nestedRules && rule.nestedRules.length > 0) {
        const found = this.findRuleContainingComponent(componentId, rule.nestedRules);
        if (found) return found;
      }
    }
    return null;
  }

  navigateToNextElement(componentId: string, itemId: string) {
    const component = this.getComponentById(componentId);
    const rule = this.findRuleContainingComponent(componentId);
    if (!component || !rule) return;

    const totalQty = this.getTotalQuantityForComponent(componentId);
    if (totalQty === component.quantity) {
      const nextComponent = this.findNextIncompleteComponentInRule(rule, componentId);
      if (nextComponent) {
        this.scrollToComponent(nextComponent.id);
        return;
      }
      const nextRule = this.findNextIncompleteRule(rule);
      if (nextRule) {
        this.scrollToRule(nextRule.id);
        return;
      }
    }
    const nextComponent = this.findNextIncompleteComponentInRule(rule, componentId);
    if (nextComponent) {
      this.scrollToComponent(nextComponent.id);
      return;
    }
    const nextRule = this.findNextIncompleteRule(rule);
    if (nextRule) this.scrollToRule(nextRule.id);
  }

  findNextIncompleteComponentInRule(rule: ProcessedRule, currentComponentId: string): any {
    const components = rule.components || [];
    const currentIndex = components.findIndex((comp: any) => comp.id === currentComponentId);
    for (let i = currentIndex + 1; i < components.length; i++) {
      const component = components[i];
      if (this.getTotalQuantityForComponent(component.id) < (component.quantity || 1)) return component;
    }
    return null;
  }

  findNextIncompleteRule(currentRule: ProcessedRule): ProcessedRule | null {
    const allRules = this.flattenRules(this.processedRules);
    const currentIndex = allRules.findIndex((r: any) => r.id === currentRule.id);
    if (currentIndex === -1) return null;
    for (let i = currentIndex + 1; i < allRules.length; i++) {
      if (!this.isRuleCompleted(allRules[i].id)) return allRules[i];
    }
    return null;
  }

  flattenRules(rules: ProcessedRule[]): ProcessedRule[] {
    const flattened: ProcessedRule[] = [];
    const flatten = (ruleList: ProcessedRule[]) => {
      for (const rule of ruleList) {
        flattened.push(rule);
        if (rule.nestedRules && rule.nestedRules.length > 0) flatten(rule.nestedRules);
      }
    };
    flatten(rules);
    return flattened;
  }

  decrementItem(componentId: string, itemId: string) {
    if (!this.selectedItemQuantities[componentId]?.[itemId]) return;
    this.selectedItemQuantities[componentId][itemId]--;

    if (this.selectedItemQuantities[componentId][itemId] <= 0) {
      this.selectedItemQuantities[componentId][itemId] = 0;
      const customKey = `${componentId}_${itemId}`;
      if (this.customizedItems[customKey]) delete this.customizedItems[customKey];
    }
  }

  getItemQuantity(componentId: string, itemId: string): number {
    return this.selectedItemQuantities[componentId]?.[itemId] || 0;
  }

  getComponentById(componentId: string): any {
    const findInRules = (rules: ProcessedRule[]): any => {
      for (const rule of rules) {
        const component = rule.components.find((c: any) => c.id === componentId);
        if (component) return component;
        if (rule.nestedRules && rule.nestedRules.length > 0) {
          const found = findInRules(rule.nestedRules);
          if (found) return found;
        }
      }
      return null;
    };
    return findInRules(this.processedRules);
  }

  getSelectedItems(): Array<{ id: string; name: string; quantity: number; componentId: string }> {
    const items: any[] = [];
    const collectFromComponents = (components: any[]) => {
      components.forEach(component => {
        component.availableItems.forEach((item: any) => {
          const qty = this.getItemQuantity(component.id, item.id);
          if (qty > 0) {
            items.push({id: item.id, name: item.name, quantity: qty, componentId: component.id});
          }
        });
      });
    };

    const collect = (rules: ProcessedRule[]) => {
      for (const rule of rules) {
        if (rule.type === 'OR' && rule.nestedRules && rule.nestedRules.length > 0) {
          const selectedGroupIds = this.selectedOrGroups[rule.id] || [];
          selectedGroupIds.forEach(groupId => {
            const group = this.findRuleById(groupId);
            if (group) collect([group]);
          });
          continue;
        }
        if (rule.type === 'OR' && (!rule.nestedRules || rule.nestedRules.length === 0)) {
          collectFromComponents(rule.components);
          continue;
        }
        if (rule.type === 'AND') {
          collectFromComponents(rule.components);
          if (rule.nestedRules && rule.nestedRules.length > 0) collect(rule.nestedRules);
        }
      }
    };
    collect(this.processedRules);
    return items;
  }

  removeItem(componentId: string, itemId: string, index: number = 0) {
    if (this.selectedItemQuantities[componentId]?.[itemId] > 0) {
      this.selectedItemQuantities[componentId][itemId]--;
    }
    const customKey = `${componentId}_${itemId}_${index}`;
    if (this.customizedItems[customKey]?.[index]) delete this.customizedItems[customKey];
  }

  canAddToCart(): boolean {
    if (!this.bundle?.selectionRules) return false;
    return this.validateRules(this.processedRules);
  }

  private validateRules(rules: ProcessedRule[]): boolean {
    for (const rule of rules) {
      if (rule.type === 'OR' && rule.nestedRules && rule.nestedRules.length > 0) {
        const selectedGroups = this.selectedOrGroups[rule.id] || [];
        if (selectedGroups.length < rule.minSelections || selectedGroups.length > rule.maxSelections) return false;
        for (const groupId of selectedGroups) {
          const group = this.findRuleById(groupId);
          if (group && !this.validateRules([group])) return false;
        }
        continue;
      }
      if (rule.type === 'OR' && rule.components.length > 0 && (!rule.nestedRules || rule.nestedRules.length === 0)) {
        let completedComponents = 0;
        for (const component of rule.components) {
          if (this.getTotalQuantityForComponent(component.id) >= (component.quantity || 1)) completedComponents++;
        }
        if (completedComponents < rule.minSelections || completedComponents > rule.maxSelections) return false;
        continue;
      }
      if (rule.type === 'AND') {
        for (const component of rule.components) {
          if (component.required && this.getTotalQuantityForComponent(component.id) < (component.quantity || 1)) return false;
        }
        if (rule.nestedRules && rule.nestedRules.length > 0 && !this.validateRules(rule.nestedRules)) return false;
      }
    }
    return !this.getSelectedItems().some(item => this.hasIncompleteRequiredCustomizations(item.componentId, item.id));
  }

  private getValidationMessageForRules(rules: ProcessedRule[]): string {
    for (const rule of rules) {
      if (rule.type === 'OR' && rule.nestedRules && rule.nestedRules.length > 0) {
        const selectedCount = this.selectedOrGroups[rule.id]?.length || 0;
        if (selectedCount < rule.minSelections) return `Veuillez sélectionner ${rule.minSelections - selectedCount} option(s) pour "${rule.name}"`;
        if (selectedCount > rule.maxSelections) return `Vous avez sélectionné trop d'options pour "${rule.name}" (maximum ${rule.maxSelections})`;
        const selectedGroups = this.selectedOrGroups[rule.id] || [];
        for (const groupId of selectedGroups) {
          const group = this.findRuleById(groupId);
          if (group) {
            const msg = this.getValidationMessageForRules([group]);
            if (msg) return msg;
          }
        }
        continue;
      }
      if (rule.type === 'OR' && rule.components.length > 0 && (!rule.nestedRules || rule.nestedRules.length === 0)) {
        let completedComponents = 0;
        const componentNames: string[] = [];

        for (const component of rule.components) {
          componentNames.push(component.category?.name || 'cet élément');
          if (this.getTotalQuantityForComponent(component.id) >= (component.quantity || 1)) completedComponents++;
        }

        if (completedComponents < rule.minSelections) {
          if (rule.components.length === 1) {
            return `Choisissez ${(rule.components[0].quantity || 1) - this.getTotalQuantityForComponent(rule.components[0].id)} produit(s) pour "${rule.components[0].category?.name || 'ce choix'}"`;
          }
          return `Choisissez ${rule.minSelections - completedComponents} option(s) parmi : ${componentNames.join(' ou ')}`;
        }
        if (completedComponents > rule.maxSelections) return `Vous ne pouvez choisir qu'${rule.maxSelections === 1 ? 'une seule' : rule.maxSelections} option parmi : ${componentNames.join(' ou ')}`;
        continue;
      }
      if (rule.type === 'AND') {
        for (const component of rule.components) {
          if (component.required) {
            const totalSelected = this.getTotalQuantityForComponent(component.id);
            const requiredQty = component.quantity || 1;
            if (totalSelected < requiredQty) return `Choisissez ${requiredQty - totalSelected} produit(s) pour "${component.category?.name}"`;
          }
        }
        if (rule.nestedRules && rule.nestedRules.length > 0) {
          const msg = this.getValidationMessageForRules(rule.nestedRules);
          if (msg) return msg;
        }
      }
    }
    const incompleteCustomizationItem = this.getSelectedItems().find(item => this.hasIncompleteRequiredCustomizations(item.componentId, item.id));
    return incompleteCustomizationItem ? `Veuillez compléter la personnalisation de "${incompleteCustomizationItem.name}"` : '';
  }

  getValidationMessage(): string {
    return this.getValidationMessageForRules(this.processedRules);
  }

  addToCart() {
    if (this.isSubmitting()) return;

    if (!this.canAddToCart()) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        this.getValidationMessage() || 'Veuillez compléter tous les choix obligatoires',
        ''
      );
      return;
    }

    const existingBundle = this.cartService.bundleItems().find(
      item => item.bundle.id === this.bundle?.id && item.id !== this.editingCartItemId
    );

    if (existingBundle) {
      if (this.isRedeemMode && !existingBundle.redeemWithPoints) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `Le menu "${this.bundle.name}" est déjà dans votre panier en version payante. Retirez-le du panier si vous souhaitez l'obtenir avec vos points.`,
          ''
        );
        return;
      }

      if (!this.isRedeemMode && existingBundle.redeemWithPoints) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `Le menu "${this.bundle.name}" est déjà dans votre panier en version points. Retirez-le du panier si vous souhaitez l'acheter en Euros.`,
          ''
        );
        return;
      }
    }

    if (this.isRedeemMode) {
      this.checkPointsThenAdd();
      return;
    }
    this.doAddToCart();
  }

  private fetchCustomerPoints() {
    return this.customerService.getProfile().pipe(
      map((res: any) => Number(res.body?.data?.loyaltyPoints ?? 0)),
      catchError(() => of(null))
    );
  }

  private getPointsAlreadyInCart(): number {
    return this.cartService.getPointsInCartExcluding(this.editingCartItemId);
  }
  private checkPointsThenAdd() {
    this.isSubmitting.set(true);
    this.fetchCustomerPoints().pipe(
      takeUntil(this.destroy$)
    ).subscribe(balance => {
      if (balance == null) {
        this.isSubmitting.set(false);
        this.notificationService.sendNotification(
          NotificationType.ERROR,
          'Connexion requise',
          'Veuillez vous connecter à votre compte pour utiliser vos points de fidélité.'
        );
        return;
      }

      const needed = this.toPoints(this.bundle.price);
      const inCart = this.getPointsAlreadyInCart();
      const available = Math.max(balance - inCart, 0);

      if (needed > available) {
        this.isSubmitting.set(false);
        let detailMsg = `Ce menu nécessite ${needed} pts, mais vous n'avez que ${available} pts disponibles.`;

        if (inCart > 0) {
          detailMsg += ` (Solde total : ${balance} pts, dont ${inCart} pts déjà réservés dans votre panier).`;
        }

        this.notificationService.sendNotification(
          NotificationType.ERROR,
          'Solde de points insuffisant',
          detailMsg
        );
        return;
      }

      this.doAddToCart();
    });
  }
  getOrderTypeLabelFromKey(key: string): string {
    const typeKey = key as keyof typeof TypeOrderEnum;
    return TypeOrderEnum[typeKey] || key;
  }

  private getOrderTypeForAPI(): string {
    const orderType = this.orderContextStore.orderType();
    switch (orderType) {
      case TypeOrderEnum.DELIVERY:
        return 'DELIVERY';
      case TypeOrderEnum.TAKEAWAY:
        return 'TAKEAWAY';
      case TypeOrderEnum.ON_SITE:
        return 'ON_SITE';
      default:
        return 'TAKEAWAY';
    }
  }

  private findEntityNameInCart(entityType: string, entityId: string): string {
    if (!entityId) return '';

    // 1. Chercher dans le bundle actuel et ses composants/produits
    if (this.bundle) {
      if (entityType === 'bundle' && (this.bundle.id === entityId || this.bundle.slug === entityId)) {
        return this.bundle.name;
      }

      // Parcourir les composants du menu
      const checkComponents = (rules: ProcessedRule[]): string => {
        for (const rule of rules) {
          for (const component of rule.components || []) {
            for (const item of component.availableItems || []) {
              const cachedProduct = this.fullProductsCache[item.id] || item;

              if (entityType === 'product' && (item.id === entityId || cachedProduct.id === entityId)) {
                return cachedProduct.name || item.name || 'Produit';
              }

              // Chercher dans les accompagnements/additions
              if (cachedProduct.additions && Array.isArray(cachedProduct.additions)) {
                for (const addition of cachedProduct.additions) {
                  if (addition.items && Array.isArray(addition.items)) {
                    const foundItem = addition.items.find((i: any) => i.id === entityId);
                    if (foundItem) return foundItem.name;
                  }
                }
              }

              // Chercher dans les suppléments
              if (cachedProduct.supplements && Array.isArray(cachedProduct.supplements)) {
                const foundSupp = cachedProduct.supplements.find((s: any) => s.id === entityId);
                if (foundSupp) return foundSupp.name;
              }

              // Chercher dans les ingrédients
              if (cachedProduct.ingredients && Array.isArray(cachedProduct.ingredients)) {
                const foundIng = cachedProduct.ingredients.find((ing: any) => ing.id === entityId);
                if (foundIng) return foundIng.name;
              }
            }
          }

          if (rule.nestedRules && rule.nestedRules.length > 0) {
            const nestedFound = checkComponents(rule.nestedRules);
            if (nestedFound) return nestedFound;
          }
        }
        return '';
      };

      const foundInRules = checkComponents(this.processedRules);
      if (foundInRules) return foundInRules;

      // Chercher dans les personnalisations en cours (deux niveaux : customKey -> index -> cust)
      for (const customKey of Object.keys(this.customizedItems || {})) {
        const custGroup = this.customizedItems[customKey] as any;
        if (!custGroup) continue;

        // Gérer aussi bien si custGroup contient des sous-indices [0], [1] ou directement l'objet
        const custItems = (typeof custGroup === 'object' && !custGroup.product && !custGroup.selectedAdditions)
          ? Object.values(custGroup)
          : [custGroup];

        for (const cust of custItems as any[]) {
          if (!cust) continue;
          const custProduct = cust.product || {};

          if (cust.selectedAdditions) {
            for (const addId of Object.keys(cust.selectedAdditions)) {
              const itemsMap = cust.selectedAdditions[addId];
              if (itemsMap && itemsMap[entityId] > 0) {
                const additionDef = custProduct.additions?.find((a: any) => a.id === addId);
                const itemDef = additionDef?.items?.find((i: any) => i.id === entityId);
                return itemDef?.name || additionDef?.name || 'Ingrédient';
              }
            }
          }

          if (cust.selectedSupplements && cust.selectedSupplements[entityId] > 0) {
            const suppDef = custProduct.supplements?.find((s: any) => s.id === entityId);
            if (suppDef) return suppDef.name;
          }

          if (cust.excludedIngredients && Array.isArray(cust.excludedIngredients)) {
            if (cust.excludedIngredients.includes(entityId)) {
              const ingDef = custProduct.ingredients?.find((i: any) => i.id === entityId);
              if (ingDef) return ingDef.name;
            }
          }
        }
      }
    }

    // 2. Fallback dans le panier existant
    for (const item of this.cartService.items()) {
      if (item.type === CartItemType.BUNDLE && (item as BundleCartItem).bundle?.id === entityId) {
        return (item as BundleCartItem).bundle.name;
      }
      if (item.type === CartItemType.PRODUCT && (item as ProductCartItem).product?.id === entityId) {
        return (item as ProductCartItem).product.name;
      }
    }

    return '';
  }

  private handleCartSaveError(err: any): void {
    const errorDetails = err.error?.details;
    const errorMsg = errorDetails?.message || err.error?.message || '';
    const errorValue = errorDetails?.value;

    if (
      (errorMsg.includes('type de commande') && errorMsg.includes('indisponible')) ||
      (err.error?.code === 'illegal_value' && ['DELIVERY', 'TAKEAWAY', 'ON_SITE'].includes(errorValue))
    ) {
      const rawType = errorValue || this.getOrderTypeForAPI();
      const translatedType = this.getOrderTypeLabelFromKey(rawType);
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le mode "${translatedType}" n'est pas disponible actuellement.`,
        'Veuillez modifier votre type de commande.'
      );
      return;
    }

    let detailMsg: string = err.error?.details?.message || err.error?.message || '';
    Object.keys(TypeOrderEnum).forEach((key) => {
      if (detailMsg.includes(key)) {
        const translatedLabel = this.getOrderTypeLabelFromKey(key);
        detailMsg = detailMsg.replace(new RegExp(key, 'g'), translatedLabel);
      }
    });

    if (detailMsg.includes('CUSTOMER_NOT_ALLOWED')) {
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Ce code promo appartient à un autre utilisateur, vous n’avez pas le droit de l’utiliser.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('NOT_STARTED_YET')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" n'est pas encore disponible.`,
        ''
      );
      return;
    }

    if (detailMsg.includes('EXPIRED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Le code promo "${promoCode}" a expiré.`,
        ''
      );
      return;
    }

    if (detailMsg === 'ORDER_RATE_LIMIT_EXCEEDED') {
      const details = err.error?.details?.details;
      let timeMessage = 'quelques minutes';
      if (details?.allowedAfter) {
        const matchMinutes = details.allowedAfter.match(/PT(?:(\d+)M)?/);
        if (matchMinutes && matchMinutes[1]) {
          timeMessage = `${matchMinutes[1]} minute(s)`;
        }
      }
      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Limite de commandes atteinte : Vous avez déjà passé ${details?.orders || 'plusieurs'} commande(s).`,
        `Par sécurité, merci de patienter encore ${timeMessage} avant de pouvoir commander à nouveau.`
      );
      return;
    }

    if (detailMsg === 'INSUFFICIENT_LOYALTY_POINTS') {
      const value = err.error?.details?.value;
      const available = value?.available ?? 0;
      const required = value?.required ?? 0;
      const missing = Math.max(0, required - available);
      const pts = (n: number) => `${n} point${n > 1 ? 's' : ''}`;

      this.notificationService.sendNotification(
        NotificationType.WARNING,
        `Points de fidélité insuffisants : vous avez ${pts(available)} et il en faut ${pts(required)}.`,
        missing > 0 ? `Il vous manque ${pts(missing)}.` : ''
      );
      return;
    }

    if (detailMsg.includes('USAGE_LIMIT_REACHED')) {
      const parts = detailMsg.split('/');
      const promoCode = parts[1] || '';
      const message = detailMsg.includes('PER_CUSTOMER')
        ? `Vous avez atteint la limite d'utilisation du code promo "${promoCode}".`
        : `Le code promo "${promoCode}" n'est plus disponible. La limite d'utilisation globale a été atteinte.`;

      this.notificationService.sendNotification(NotificationType.WARNING, message, '');
      return;
    }

    if (err.status === 404 || detailMsg.includes('/')) {
      if (detailMsg.startsWith('DeliveryCity/')) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `La ville de livraison n'est plus disponible. Merci de choisir une autre ville.`,
          ''
        );
        return;
      }

      const slashIndex = detailMsg.indexOf('/');
      const entityType = slashIndex !== -1 ? detailMsg.substring(0, slashIndex).toLowerCase() : '';
      const entityId = slashIndex !== -1 ? detailMsg.substring(slashIndex + 1).trim() : '';
      const foundName = this.findEntityNameInCart(entityType, entityId);

      const typeLabels: Record<string, string> = {
        promotion: 'La promotion',
        product: 'Le produit',
        bundle: 'Le menu',
        productingredient: "L'ingrédient",
        productsupplement: 'Le supplément'
      };
      const typeLabel = typeLabels[entityType] || "L'élément";

      if (foundName) {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} "${foundName}" n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      } else {
        this.notificationService.sendNotification(
          NotificationType.WARNING,
          `${typeLabel} n'est plus disponible. Merci de réviser votre sélection.`,
          ''
        );
      }
      return;
    }

    if (detailMsg) {
      this.notificationService.sendNotification(NotificationType.WARNING, detailMsg, '');
      return;
    }

    this.notificationService.errorHandler(err);
  }
  private doAddToCart() {
    this.isSubmitting.set(true);
    const previousCartItemsSnapshot = JSON.parse(JSON.stringify(this.cartService.items()));

    if (this.editingCartItemId) {
      this.cartService.removeItem(this.editingCartItemId);
    }

    const formattedCustomizedItems: { [key: string]: any } = {};
    Object.keys(this.selectedItemQuantities).forEach(componentId => {
      Object.keys(this.selectedItemQuantities[componentId]).forEach(itemId => {
        const quantity = this.selectedItemQuantities[componentId][itemId];
        if (quantity > 0) {
          for (let index = 0; index < quantity; index++) {
            const customKey = `${componentId}_${itemId}_${index}`;
            const customization = this.customizedItems[customKey];
            if (customization) {
              formattedCustomizedItems[customKey] = JSON.parse(JSON.stringify(customization));
            }
          }
        }
      });
    });

    const redeem = this.isRedeemMode;

    try {
      this.cartService.addBundle({
        bundle: this.bundle,
        slug: this.bundleName,
        bundleImage: this.bundle.image || '',
        selectedItems: JSON.parse(JSON.stringify(this.selectedItemQuantities)),
        customizedItems: formattedCustomizedItems,
        total: redeem ? this.getExtrasPrice() : this.getTotalCartPrice(),
        redeemWithPoints: redeem,
        pointsCost: redeem ? this.toPoints(this.bundle.price) : null
      });

      if (this.isCartSheetOpen) this.toggleCartSheet();

      const apiOrderType = this.cartService.getOrderTypeForAPI();
      this.cartService.saveCartToServer(apiOrderType).subscribe({
        next: () => {
          // Reste disabled
          this.editingCartItemId = null;
          if (this.returnToCommand) {
            this.router.navigate(['/command'], { queryParams: redeem ? { mode: 'redeem' } : {} });
          } else {
            this.notificationService.sendNotification(
              NotificationType.SUCCESS,
              'Menu mis à jour dans le panier !',
              ''
            );
            this.router.navigate(['/product'], { queryParams: redeem ? { mode: 'redeem' } : {} });
          }
        },
        error: (err) => {
          this.isSubmitting.set(false);
          this.cartService.restoreItems(previousCartItemsSnapshot);
          this.handleCartSaveError(err);
        }
      });
    } catch (error) {
      this.isSubmitting.set(false);
      this.cartService.restoreItems(previousCartItemsSnapshot);
      console.error('Error adding bundle to cart:', error);
      this.notificationService.sendNotification(NotificationType.ERROR, 'Erreur lors de la mise à jour du panier', '');
    }
  }
  openProductCustomization(item: any, componentId: string, index: number = 0, isAutoOpen: boolean = false) {
    const customKey = `${componentId}_${item.id}_${index}`;
    const existingCustomization = isAutoOpen ? undefined : this.customizedItems[customKey];
    const component = this.getComponentById(componentId);

    this.dialog.open(DetailsProductMenusComponent, {
      width: '95%',
      maxWidth: '600px',
      panelClass: 'custom-dialog',
      data: {
        productId: item.id,
        product: this.fullProductsCache[item.id] || null,
        existingCustomization: existingCustomization,
        preselectedSize: component?.size?.name || null
      }
    }).afterClosed().pipe(takeUntil(this.destroy$)).subscribe(result => {
      if (result) this.customizedItems[customKey] = result;
    });
  }

  getCustomizationDetails = (componentId: string, itemId: string, index: number = 0): any => this.customizedItems[`${componentId}_${itemId}_${index}`];
  isItemCustomized = (componentId: string, itemId: string, index: number = 0): boolean => !!this.customizedItems[`${componentId}_${itemId}_${index}`];

  getCustomizationSummary(componentId: string, itemId: string, index: number = 0): string {
    const customization = this.getCustomizationDetails(componentId, itemId, index);
    if (!customization) return '';

    const parts: string[] = [];
    if (customization.excludedIngredients?.length > 0) {
      const excludedNames = customization.excludedIngredients.map((id: string) => customization.product.ingredients?.find((i: any) => i.id === id)?.name).filter(Boolean);
      if (excludedNames.length > 0) parts.push(`❌ Sans: ${excludedNames.join(', ')}`);
    }

    if (customization.selectedAdditions) {
      const additionParts: string[] = [];
      const selectedSize = customization.selectedSize || this.getComponentById(componentId)?.size?.name || null;
      const filteredAdditions = this.getFilteredAdditionsForProduct(customization.product.additions || [], selectedSize);

      Object.keys(customization.selectedAdditions).forEach(additionId => {
        const addition = filteredAdditions.find((a: any) => a.id === additionId);
        if (addition) {
          Object.keys(customization.selectedAdditions[additionId]).forEach(itemAdditionId => {
            const qty = customization.selectedAdditions[additionId][itemAdditionId];
            if (qty > 0) {
              const addItem = addition.items.find((i: any) => i.id === itemAdditionId);
              if (addItem) additionParts.push(`${addItem.name}${qty > 1 ? ` x${qty}` : ''}`);
            }
          });
        }
      });
      if (additionParts.length > 0) parts.push(`🍽️ Choix: ${additionParts.join(', ')}`);
    }

    if (customization.selectedSupplements && Object.keys(customization.selectedSupplements).length > 0) {
      const supplementParts: string[] = [];
      let totalSuppPrice = 0;

      Object.keys(customization.selectedSupplements).forEach(suppId => {
        const supplement = customization.product.supplements?.find((s: any) => s.id === suppId);
        if (supplement) {
          const qty = customization.selectedSupplements[suppId] || 1;
          let price = supplement.price || 0;
          if (customization.selectedSize && supplement.pricePerSizes?.length > 0) {
            price = supplement.pricePerSizes.find((ps: any) => ps.size.name === customization.selectedSize)?.price || price;
          }
          const itemTotal = price * qty;
          totalSuppPrice += itemTotal;
          supplementParts.push(`[CAT]${supplement.categories?.[0] || ''}-${supplement.name}${qty > 1 ? ` x${qty}` : ''}${itemTotal > 0 ? ` (+${this.truncate2(itemTotal).toFixed(2).replace('.', ',')}€)` : ''}`);
        }
      });
      if (supplementParts.length > 0) parts.push(`➕ Suppléments ${totalSuppPrice > 0 ? `(${this.truncate2(totalSuppPrice).toFixed(2).replace('.', ',')}€)` : ''}: ${supplementParts.join('|||')}`);
    }
    return parts.join('\n') || 'Personnalisé';
  }

  getCustomizationExtraPrice(componentId: string, itemId: string, index: number = 0): number {
    const customization = this.getCustomizationDetails(componentId, itemId, index);
    if (!customization) return 0;

    let extraPrice = 0;
    if (customization.selectedAdditions) {
      Object.keys(customization.selectedAdditions).forEach(additionId => {
        const addition = customization.product.additions?.find((a: any) => a.id === additionId);
        if (addition) {
          Object.keys(customization.selectedAdditions[additionId]).forEach(itemAdditionId => {
            const qty = customization.selectedAdditions[additionId][itemAdditionId];
            if (qty > 0 && addition.items.find((i: any) => i.id === itemAdditionId)?.price) {
              extraPrice += addition.items.find((i: any) => i.id === itemAdditionId).price * qty;
            }
          });
        }
      });
    }

    if (customization.selectedSupplements) {
      Object.keys(customization.selectedSupplements).forEach(suppId => {
        const supplement = customization.product.supplements?.find((s: any) => s.id === suppId);
        if (supplement) {
          extraPrice += (customization.selectedSize && supplement.pricePerSizes?.length > 0)
            ? (supplement.pricePerSizes.find((ps: any) => ps.size.name === customization.selectedSize)?.price || supplement.price || 0)
            : (supplement.price || 0);
        }
      });
    }
    return extraPrice;
  }

  getTotalCartPrice(): number {
    let total = this.bundle?.price || 0;
    this.getSelectedItems().forEach(item => {
      for (let index = 0; index < item.quantity; index++) {
        total += this.getCustomizationExtraPrice(item.componentId, item.id, index);
      }
    });
    return this.truncate2(total);
  }

  private getFilteredAdditionsForProduct(additions: any[], selectedSize: string | null): any[] {
    if (!additions || additions.length === 0) return [];
    if (!selectedSize) return additions;
    return additions.filter((addition: any) => !addition.sizes || addition.sizes.length === 0 || addition.sizes.some((size: any) => size.name === selectedSize));
  }

  hasIncompleteRequiredCustomizations(componentId: string, itemId: string, index: number = 0): boolean {
    if (!this.productRequiredAdditions[itemId]) return false;
    if (index < this.getItemQuantity(componentId, itemId) && !this.isItemCustomized(componentId, itemId, index)) return true;

    const customization = this.getCustomizationDetails(componentId, itemId, index);
    if (customization?.product?.additions?.length > 0) {
      const selectedSize = customization.selectedSize || this.getComponentById(componentId)?.size?.name || null;
      const filteredAdditions = this.getFilteredAdditionsForProduct(customization.product.additions, selectedSize);

      for (const addition of filteredAdditions) {
        if (addition.required && this.getTotalSelectedForAddition(customization, addition.id) !== (addition.quantity || 1)) return true;
      }
    }
    return false;
  }

  getTotalSelectedForAddition(customization: any, additionId: string): number {
    if (!customization.selectedAdditions || !customization.selectedAdditions[additionId]) return 0;
    return Object.values(customization.selectedAdditions[additionId] as Record<string, number>).reduce((sum: number, qty: number) => sum + qty, 0);
  }

  getSizeText = (component: any): string => component?.size?.name || '';
  getOrderTypeLabel = (): string => {
    const labels: Record<TypeOrderEnum, string> = {
      [TypeOrderEnum.DELIVERY]: 'En Livraison',
      [TypeOrderEnum.TAKEAWAY]: 'À Emporter',
      [TypeOrderEnum.ON_SITE]: 'Sur Place'
    };
    return labels[this.selectedOrderType()!] || '';
  };

  getMinOrderAmount(): string {
    const amounts = this.minOrderAmounts();
    if (!amounts || amounts.length === 0) return '';
    const currentSeconds = (() => {
      const [h, m, s] = new Date().toTimeString().split(' ')[0].split(':').map(Number);
      return h * 3600 + m * 60 + s;
    })();
    const toSec = (t: string) => {
      const [h, m, s] = t.split(':').map(Number);
      return h * 3600 + m * 60 + s;
    };
    for (const slot of amounts) {
      const from = toSec(slot.from), to = toSec(slot.to);
      if ((from <= to && currentSeconds >= from && currentSeconds <= to) || (from > to && (currentSeconds >= from || currentSeconds <= to))) return slot.amount === 0 ? '' : `Min Cmd: ${slot.amount.toFixed(2)}€`;
    }
    return amounts[0].amount === 0 ? '' : `Min Cmd: ${amounts[0].amount.toFixed(2)}€`;
  }


  getFormattedLocation() {
    if (this.isTakeawayOrOnSite) {
      return {
        text: this.restaurantAddress() || '',
        hasAddress: !!this.restaurantAddress()
      };
    }

    const city = this.cityName() || '';
    const zip = this.zipCode();
    const text = zip ? `${city} ${zip}` : city;

    return {text, hasAddress: false};
  }

  formatDeliveryDelay(): string {
    const delay = this.deliveryDelay();
    if (!delay) return '';
    const match = delay.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
    if (!match) return '';
    const h = match[1] ? parseInt(match[1]) : 0, m = match[2] ? parseInt(match[2]) : 0;
    return h > 0 && m > 0 ? `${h}h ${m}min` : h > 0 ? `${h}h` : `${m}min`;
  }

  openDialog(categoryId?: string) {
    this.dialog.open(OrderComponent, {
      panelClass: 'custom-dialog',
      autoFocus: false,
      disableClose: true,
      data: {
        selectedCategoryId: categoryId,
        context: 'menus',
        bundleSlug: this.bundleName,
        promoId: this.bundleId,
        stayOnMenusPage: true
      }
    });
  }

  truncate2 = (v: number) => Math.floor(v * 100 + 1e-6) / 100;

  private setSeoTags() {
    this.route.title.subscribe(title => {
      this.titleService.setTitle(`${title} - ${this.bundle?.name}`)
    })

    const keywords = this.extractBundleKeywords();
    const description = this.generateBundleDescription();

    this.addTag({name: 'title', content: `${this.bundle?.name} | ${this.configStore.name()}`});
    this.addTag({name: 'description', content: description});
    this.addTag({name: 'keywords', content: keywords});
    this.addTag({name: 'bundle:price:amount', content: this.bundle.price.toString()});
    this.addTag({name: 'bundle:price:currency', content: this.bundle.currency || '€'});

    this.addTag({property: 'og:title', content: this.bundle.name});
    this.addTag({property: 'og:description', content: description.substring(0, 300)});
    this.addTag({property: 'og:image', content: this.bundle.image});
    this.addTag({property: 'og:image:alt', content: this.bundle.name});
    this.addTag({property: 'og:type', content: 'product.group'});
    this.addTag({property: 'og:price:amount', content: this.bundle.price.toString()});
    this.addTag({property: 'og:price:currency', content: this.bundle.currency || '€'});

    this.addTag({name: 'twitter:card', content: 'summary_large_image'});
    this.addTag({name: 'twitter:title', content: this.bundle.name});
    this.addTag({name: 'twitter:description', content: description.substring(0, 200)});
    this.addTag({name: 'twitter:image', content: this.bundle.image});
  }

  private extractBundleKeywords(): string {
    const keywords = [this.bundle.name];

    if (this.bundle.selectionRules) {
      this.bundle.selectionRules.forEach((rule: any) => {
        rule.components?.forEach((component: any) => {
          if (component.category?.name) {
            keywords.push(component.category.name);
          }
        });

        rule.nestedRules?.forEach((nestedRule: any) => {
          nestedRule.components?.forEach((component: any) => {
            if (component.category?.name) {
              keywords.push(component.category.name);
            }
          });
        });
      });
    }

    const uniqueKeywords = [...new Set(keywords)];
    return uniqueKeywords.slice(0, 15).join(', ');
  }

  private generateBundleDescription(): string {
    if (this.bundle.description) {
      return this.bundle.description;
    }

    const parts = [`${this.bundle.name} à ${this.bundle.formattedPrice}`];

    const includedItems: string[] = [];

    if (this.bundle.selectionRules) {
      this.bundle.selectionRules.forEach((rule: any) => {
        rule.components?.forEach((component: any) => {
          if (component.required && component.category?.name) {
            const quantity = component.quantity || 1;
            includedItems.push(`${quantity}x ${component.category.name}`);
          }
        });

        rule.nestedRules?.forEach((nestedRule: any) => {
          const categoryNames = nestedRule.components
            ?.filter((comp: any) => comp.category?.name)
            .map((comp: any) => comp.category.name);

          if (categoryNames?.length) {
            const choices = categoryNames.join(' ou ');
            const selectionText = nestedRule.minSelections === nestedRule.maxSelections
              ? `Choisir ${nestedRule.minSelections} from ${choices}`
              : `Choisir ${nestedRule.minSelections}-${nestedRule.maxSelections} depuis ${choices}`;
            includedItems.push(selectionText);
          }
        });
      });
    }

    if (includedItems.length) {
      parts.push(`Comprend: ${includedItems.join(', ')}`);
    }

    return parts.join('. ');
  }

  private addTag(tag: MetaDefinition, forceCreation?: boolean) {
    const t = this.metaService.addTag(tag, forceCreation)
    if (t) this.metaTags.push(t)
  }

  private removeSeoTags() {
    this.metaTags.forEach(tag => this.metaService.removeTagElement(tag))
    console.log(`Cleared ${this.metaTags.length} tags`)
    this.metaTags = []
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.removeSeoTags();
    this.hideFloatingCartService.setHide(false);
    if (this.isBrowser) {
      document.body.style.overflow = '';
    }
    if (this.isBrowser && !this.router.url.includes('/product')) {
      sessionStorage.removeItem('productPageState');
    }
  }

  trackItem(item: string, index: number): string {
    return `${item}-${index}`;
  }
}
